import React, { Fragment } from 'react'
import styled from '@emotion/styled'
import { classNames } from 'common/emotion'
import { StyledPicture } from 'component-library/content/Picture/Picture.styled'
import { Image } from 'component-library/content/Picture/Image.web'
import { StyledNoScriptImage } from 'component-library/content/Picture/Image.noscript'
import { ImageSrcInfo } from './helpers/srcSet'
import { ImageSource, BreakpointRatios, FixedRatio } from 'common/client-data-types/image-item'
import {
    getPictureSources,
    getCrops,
    getPlaceholderRatio,
    fixedRatioToBreakpointRatios,
    useTrackIfPicureIsNotAvailable,
} from '../utils'
import { LazyLoadedPicture } from '../LazyLoadedPicture'

export interface FillContainerHorizontallyPictureProps {
    image: ImageSource
    srcInfo: ImageSrcInfo
    imageSizes?: string
    className?: string
    fixedRatio?: FixedRatio | BreakpointRatios
    disableLazyLoad?: boolean
    renderPicture: (pic: React.ReactElement) => React.ReactElement
}

const Picture = styled(StyledPicture)({
    position: 'relative',
    display: 'block',

    '& img': {
        width: '100%',
        height: 'auto',
        display: 'block',
        position: 'absolute',
        top: 0,
        left: 0,
    },
})

export const FillContainerHorizontallyPicture: React.FC<FillContainerHorizontallyPictureProps> = ({
    image,
    srcInfo,
    imageSizes,
    className,
    fixedRatio = 'original',
    disableLazyLoad,
    renderPicture,
}) => {
    const { srcCallback, initialWidth } = srcInfo

    const breakpointRatios = fixedRatioToBreakpointRatios(fixedRatio)
    const breakpointCrops = getCrops(image.crops, breakpointRatios, false)

    const isUnavailable = !breakpointCrops
    useTrackIfPicureIsNotAvailable(isUnavailable, image)

    if (!breakpointCrops) {
        return null
    }

    const pictureSources = getPictureSources(breakpointCrops, imageSizes, srcInfo)
    const defaultSrc = srcCallback(breakpointCrops.default.reference, initialWidth)

    return renderPicture(
        <LazyLoadedPicture disableLazyLoad={disableLazyLoad}>
            {({ lazyLoadRef, lazyLoadHasTriggered: loaded, wasLazyLoaded }) => (
                <Picture
                    placeholderBreakpointRatios={getPlaceholderRatio(image.crops, breakpointRatios)}
                    className={classNames(className, 'image')}
                    ref={lazyLoadRef}
                >
                    {loaded && (
                        <Fragment>
                            {pictureSources}
                            <Image
                                src={defaultSrc}
                                alt={image.altText}
                                enableFadeIn={wasLazyLoaded}
                            />
                        </Fragment>
                    )}
                    <StyledNoScriptImage show={true} alt={image.altText} src={defaultSrc} />
                </Picture>
            )}
        </LazyLoadedPicture>
    )
}

FillContainerHorizontallyPicture.displayName = 'FillContainerHorizontallyPicture'
